import React, { useState, useEffect, useRef } from 'react';
import fileService from '../services/fileService';

/**
 * ProtectedAsset Component
 * 
 * This component handles loading of protected assets from your backend API.
 * It intercepts asset URLs and fetches them with proper authentication.
 * 
 * Usage:
 * <ProtectedAsset 
 *   src="/assets/7/lgXax4lDvV" 
 *   alt="Protected image"
 *   className="w-full h-auto"
 *   fileName="PHOTO-2025-02-19-18-08-24.jpg"
 * />
 */
const ProtectedAsset = ({ 
  src, 
  alt = '', 
  className = '', 
  style = {}, 
  fileName = null,
  onLoad = null,
  onError = null,
  placeholder = null,
  ...props 
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let currentBlobUrl = null;

    const loadAsset = async () => {
      if (!src) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Extract asset path from src
        // Handle both "/assets/7/lgXax4lDvV" and "assets/7/lgXax4lDvV"
        const assetPath = src.replace(/^\/assets\//, '').replace(/^assets\//, '');
        
        if (!assetPath) {
          throw new Error('Invalid asset path');
        }

        // Fetch the protected asset
        const url = await fileService.fetchProtectedAsset(assetPath, fileName);
        
        if (isMounted) {
          currentBlobUrl = url;
          setBlobUrl(url);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load protected asset:', err);
        if (isMounted) {
          setError(err.message);
          setLoading(false);
          if (onError) {
            onError(err);
          }
        }
      }
    };

    loadAsset();

    // Cleanup function
    return () => {
      isMounted = false;
      if (currentBlobUrl) {
        window.URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [src, fileName, onError]);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const handleLoad = () => {
    if (onLoad) {
      onLoad();
    }
  };

  const handleError = (e) => {
    console.error('Image load error:', e);
    setError('Failed to display image');
    if (onError) {
      onError(new Error('Failed to display image'));
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 ${className}`}
        style={style}
        {...props}
      >
        {placeholder || (
          <div className="flex flex-col items-center justify-center p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mb-2"></div>
            <span className="text-sm text-gray-500">Loading...</span>
          </div>
        )}
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div 
        className={`flex items-center justify-center bg-red-50 border border-red-200 ${className}`}
        style={style}
        {...props}
      >
        <div className="flex flex-col items-center justify-center p-4">
          <svg className="h-8 w-8 text-red-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span className="text-sm text-red-600 text-center">{error}</span>
        </div>
      </div>
    );
  }

  // Render the image with blob URL
  return (
    <img
      ref={imgRef}
      src={blobUrl}
      alt={alt}
      className={className}
      style={style}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
};

export default ProtectedAsset; 